import { useReducer, useCallback, useRef, useEffect, useState } from 'react';
import type {
  Message,
  MessageType,
  ToolCall,
  SourceCitation,
  ChatWidgetProps,
  SessionConfig,
  RetryPolicy,
  RichContent,
  MessageResult,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { STORAGE_KEY } from '../constants/storage';
import { chatReducer, initialChatState } from './chatReducer';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { getSessionId } from '../utils/session';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';
import { createStreamingMessageFn } from './streamingMessage';

interface UseChatOptions {
  enableStreaming: boolean;
  enableToolVisualization: boolean;
  enableRichMessages: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  streamEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  provider?: string;
  subject: string;
  department: string;
  channel: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  threadId: string | null;
  threadBase: string | null;
  companyName: string;
  saveHistory: boolean;
  loadHistory?: boolean;
  historyStorage?: 'local' | 'session';
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  parseResponse?: ChatWidgetProps['parseResponse'];
  onSend?: ChatWidgetProps['onSend'];
  onReceive?: ChatWidgetProps['onReceive'];
  onError?: ChatWidgetProps['onError'];
  onMessageCreate?: ChatWidgetProps['onMessageCreate'];
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  trackOutcome?: (outcome: unknown) => void;
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>;
  fetchThread: () => Promise<void>;
  createThread: (args?: {
    subject?: string;
    message?: string;
    meta?: Record<string, unknown>;
    ch?: string;
  }) => Promise<{ threadId: string; raw: unknown }>;
  threadCreateEndpoint: string | null | undefined;
  minimized: boolean;
  setThreadId: (id: string | null) => void;
  sessionConfig?: SessionConfig;
  retryPolicy?: RetryPolicy;
}

type SendResult = {
  ok: boolean;
  aborted?: boolean;
};

export function useChat(options: UseChatOptions) {
  const {
    enableStreaming,
    enableToolVisualization,
    enableRichMessages,
    enableSourceCitations,
    enableThinking,
    streamEndpoint,
    messageEndpoint,
    messageCreateEndpoint,
    apiBaseUrl,
    subject,
    provider,
    department,
    channel,
    customFields,
    metaExtras,
    threadId,
    threadBase,
    companyName,
    saveHistory,
    loadHistory = true,
    historyStorage = 'local',
    buildMessagePayload,
    parseResponse,
    onSend,
    onReceive,
    onError,
    onMessageCreate,
    trackAnalytics,
    trackOutcome,
    withAuthHeaders,
    fetchThread,
    createThread,
    threadCreateEndpoint,
    setThreadId,
    sessionConfig,
    retryPolicy,
  } = options;

  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [historyLoaded, setHistoryLoaded] = useState(!(loadHistory && saveHistory));
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const stateRef = useRef(state);

  const abortActiveStream = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Abort in-flight stream and mark unmounted
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortActiveStream();
    };
  }, [abortActiveStream]);

  // Load history from storage on mount
  useEffect(() => {
    if (!loadHistory || !saveHistory) {
      setHistoryLoaded(true);
      return;
    }
    try {
      const store = historyStorage === 'session' ? sessionStorage : localStorage;
      const raw = store.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) dispatch({ type: 'SET_MESSAGES', messages: parsed });
      }
    } catch {
      // storage may be unavailable (private browsing, storage full, SSR)
    } finally {
      setHistoryLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMessages = useCallback((updater: Message[] | ((prev: Message[]) => Message[])) => {
    const next = typeof updater === 'function' ? updater(stateRef.current.messages) : updater;
    dispatch({ type: 'SET_MESSAGES', messages: next });
  }, []);

  const handleJsonResponse = useCallback(
    async (
      data: Record<string, unknown>,
      localMsg: Message,
      atts: { name: string; type: string; size: number; dataUrl?: string }[]
    ): Promise<MessageResult> => {
      const parsed = typeof parseResponse === 'function' ? parseResponse(data) : null;

      let messageType: MessageType = MESSAGE_TYPES.TEXT;
      let richContent: RichContent | undefined = undefined;

      if (enableRichMessages && data.type) {
        messageType = data.type as MessageType;
        richContent = ((data as Record<string, unknown>).content ||
          (data as Record<string, unknown>).richContent) as RichContent;
      }

      if (enableRichMessages && messageType === MESSAGE_TYPES.PRODUCT_CARDS && !richContent) {
        const products =
          (data as { products?: unknown; recommendations?: unknown }).products ||
          (data as { recommendations?: unknown }).recommendations;
        if (Array.isArray(products)) {
          richContent = { products: products as RichContent['products'] };
        }
      }

      const reply: Message = {
        id: `${getNextMessageId()}-ai`,
        sender: 'ai',
        text:
          (parsed as { text?: string } | null)?.text ??
          (data as { reply?: string }).reply ??
          (data as { message?: string }).message ??
          (data as { text?: string }).text ??
          'Thanks! We received your message.',
        timestamp: new Date().toLocaleTimeString(),
        status: 'delivered',
        type: messageType,
        richContent,
        suggestions:
          (parsed as { suggestions?: string[] } | null)?.suggestions ??
          (data as { suggestions?: string[] }).suggestions ??
          [],
        sources: enableSourceCitations
          ? (((data as { sources?: unknown; citations?: unknown }).sources ||
              (data as { citations?: unknown }).citations) as SourceCitation[] | undefined)
          : undefined,
        toolCalls: enableToolVisualization
          ? ((data as { toolCalls?: unknown }).toolCalls as ToolCall[] | undefined)
          : undefined,
      };

      if (messageType === MESSAGE_TYPES.PRODUCT_CARDS && richContent?.products?.length) {
        trackAnalytics('product_recommendations', {
          count: richContent.products.length,
          ids: richContent.products.map(p => p?.id).filter(Boolean),
        });
      }

      const newThreadId =
        (parsed as { threadId?: string } | null)?.threadId ??
        (data as { threadId?: string }).threadId ??
        (data as { thread?: { id?: string } }).thread?.id ??
        null;
      if (newThreadId && newThreadId !== threadId) setThreadId(newThreadId);

      const rawOutcome =
        (data as { outcomes?: unknown }).outcomes ??
        (data as { outcome?: unknown }).outcome ??
        (data as { agenticOutcomes?: unknown }).agenticOutcomes ??
        (data as { agentic_outcomes?: unknown }).agentic_outcomes ??
        (data as { agenticOutcome?: unknown }).agenticOutcome ??
        (data as { agentic_outcome?: unknown }).agentic_outcome;
      if (trackOutcome && rawOutcome !== undefined) {
        const attachThread = (value: unknown) => {
          const effectiveThreadId = newThreadId || threadId;
          if (typeof value === 'string') return { name: value, threadId: effectiveThreadId };
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const obj = value as Record<string, unknown>;
            return { ...obj, threadId: (obj.threadId as unknown) ?? effectiveThreadId };
          }
          return value;
        };
        if (Array.isArray(rawOutcome)) {
          trackOutcome(rawOutcome.map(attachThread));
        } else {
          trackOutcome(attachThread(rawOutcome));
        }
      }

      if ((data as { escalated?: boolean }).escalated) {
        dispatch({ type: 'SET_ESCALATED', isEscalated: true });
      }

      dispatch({ type: 'ADD_MESSAGE', message: reply });
      onMessageCreate?.(localMsg, reply);
      trackAnalytics('message_sent', { hasAttachments: atts?.length > 0 });
      try {
        await fetchThread();
      } catch {
        // Thread refresh after message send is best-effort; failure is non-critical
      }
      return { request: localMsg, response: reply, raw: data };
    },
    [
      parseResponse,
      enableRichMessages,
      enableSourceCitations,
      enableToolVisualization,
      threadId,
      setThreadId,
      onMessageCreate,
      trackAnalytics,
      trackOutcome,
      fetchThread,
    ]
  );

  const readFileAsDataUrl = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.readAsDataURL(file);
    });
  }, []);

  const buildAttachmentPayload = useCallback(
    async (
      attachments: { name: string; type: string; size: number; file?: File }[]
    ): Promise<{ name: string; type: string; size: number; dataUrl?: string }[]> => {
      if (!attachments || attachments.length === 0) return [];
      return Promise.all(
        attachments.map(async att => {
          const base = { name: att.name, type: att.type, size: att.size };
          if (att.file && typeof FileReader !== 'undefined') {
            try {
              const dataUrl = await readFileAsDataUrl(att.file);
              return { ...base, dataUrl };
            } catch {
              return base;
            }
          }
          return base;
        })
      );
    },
    [readFileAsDataUrl]
  );

  // Shared payload builder for streaming and non-streaming
  const buildPayload = useCallback(
    ({
      text,
      attachments: atts = [],
      subject: subj,
      tid,
      meta,
    }: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      meta?: Record<string, unknown>;
    }) => ({
      subject: subj || subject || (text ? text.slice(0, 80) : 'New message'),
      message: text,
      attachments: (atts || []).map(a => ({
        name: a.name,
        type: a.type,
        size: a.size,
        ...(a.dataUrl ? { dataUrl: a.dataUrl } : {}),
      })),
      meta: { department, customFields, ...(metaExtras || {}), ...(meta || {}) },
      sessionId: getSessionId(sessionConfig),
      source: 'response-chat-widget',
      ...(provider ? { provider } : {}),
      channel,
      threadId: tid || threadId,
      threadMessages: state.messages.slice(-30).map(msg => ({
        id: msg.id,
        sender: msg.sender,
        text: msg.text,
        timestamp: msg.timestamp,
        type: msg.type,
        status: msg.status,
      })),
    }),
    [
      subject,
      department,
      customFields,
      metaExtras,
      provider,
      channel,
      threadId,
      sessionConfig,
      state.messages,
    ]
  );

  const fetchWithRetry = useCallback(
    async (url: string, init: RequestInit & { timeoutMs?: number }) => {
      const retries = retryPolicy?.retries ?? 0;
      const backoffMs = retryPolicy?.backoffMs ?? 500;
      const retryStatuses = retryPolicy?.retryOnStatuses ?? [429, 500, 502, 503, 504];

      let attempt = 0;
      while (true) {
        try {
          const res = await fetchWithTimeout(url, init);
          if (!res.ok && attempt < retries && retryStatuses.includes(res.status)) {
            attempt += 1;
            await new Promise(resolve =>
              setTimeout(resolve, Math.min(backoffMs * attempt, 10_000))
            );
            continue;
          }
          return res;
        } catch (err) {
          if (attempt < retries) {
            attempt += 1;
            await new Promise(resolve =>
              setTimeout(resolve, Math.min(backoffMs * attempt, 10_000))
            );
            continue;
          }
          throw err;
        }
      }
    },
    [retryPolicy]
  );

  // Streaming message handler (delegated to streamingMessage.ts)
  const createStreamingMessage = useCallback(
    (args: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      sender?: 'user' | 'ai';
      meta?: Record<string, unknown>;
    }) =>
      createStreamingMessageFn({
        ...args,
        streamEndpoint,
        messageCreateEndpoint,
        messageEndpoint,
        apiBaseUrl,
        threadId,
        threadBase,
        dispatch,
        mountedRef,
        abortControllerRef,
        withAuthHeaders,
        buildPayload,
        buildMessagePayload,
        handleJsonResponse,
        onMessageCreate,
        trackAnalytics,
        trackOutcome,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        enableRichMessages,
      }),
    [
      streamEndpoint,
      messageCreateEndpoint,
      messageEndpoint,
      apiBaseUrl,
      threadId,
      threadBase,
      withAuthHeaders,
      buildMessagePayload,
      buildPayload,
      handleJsonResponse,
      onMessageCreate,
      trackAnalytics,
      trackOutcome,
      enableToolVisualization,
      enableSourceCitations,
      enableThinking,
      enableRichMessages,
    ]
  );

  const createMessage = useCallback(
    async ({
      text,
      attachments: atts = [],
      subject: subj,
      tid,
      sender = 'user',
      meta,
    }: {
      text: string;
      attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
      subject?: string;
      tid?: string;
      sender?: 'user' | 'ai';
      meta?: Record<string, unknown>;
    }): Promise<MessageResult> => {
      if (enableStreaming && (streamEndpoint || messageEndpoint)) {
        return createStreamingMessage({
          text,
          attachments: atts,
          subject: subj,
          tid,
          sender,
          meta,
        });
      }

      const endpoint = resolveEndpoint({
        messageCreate: messageCreateEndpoint,
        message: messageEndpoint,
        threadBase,
        tid: tid || threadId,
        apiBaseUrl,
      });
      if (!endpoint) throw new Error('No message endpoint configured');

      const now = new Date();
      const localMsg: Message = {
        id: getNextMessageId(),
        sender,
        text,
        timestamp: now.toLocaleTimeString(),
        status: 'sent',
      };
      if (!(meta && typeof meta === 'object' && meta.silent === true)) {
        dispatch({ type: 'ADD_MESSAGE', message: localMsg });
      }

      const payload = buildPayload({ text, attachments: atts, subject: subj, tid, meta });
      const bodyPayload =
        typeof buildMessagePayload === 'function'
          ? buildMessagePayload(payload, localMsg)
          : payload;
      const res = await fetchWithRetry(endpoint, {
        method: 'POST',
        headers: withAuthHeaders(),
        body: JSON.stringify(bodyPayload),
        timeoutMs: 30_000,
      });
      if (!res.ok) {
        let serverError = '';
        try {
          const json = (await res.json()) as Record<string, unknown>;
          if (typeof json?.error === 'string') {
            serverError = json.error;
          } else if (typeof json?.message === 'string') {
            serverError = json.message;
          }
        } catch {
          // Keep generic status code message if body is not JSON.
        }
        throw new Error(
          `Message create failed: ${res.status}${serverError ? ` (${serverError})` : ''}`
        );
      }
      const data = await res.json();
      return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts);
    },
    [
      enableStreaming,
      streamEndpoint,
      createStreamingMessage,
      messageCreateEndpoint,
      messageEndpoint,
      apiBaseUrl,
      threadId,
      threadBase,
      withAuthHeaders,
      buildMessagePayload,
      buildPayload,
      handleJsonResponse,
      fetchWithRetry,
    ]
  );

  const handleSend = useCallback(
    async (
      attachments: {
        id: string;
        name: string;
        type: string;
        size: number;
        file?: File;
        previewUrl?: string | null;
      }[]
    ): Promise<SendResult> => {
      const inputBeforeSend = state.input;
      const text = inputBeforeSend.trim();
      if (!text && attachments.length === 0) return { ok: false };
      dispatch({ type: 'SET_INPUT', input: '' });
      dispatch({ type: 'SEND_START' });

      try {
        let ensuredThreadId = threadId;
        const subj = subject || (text ? text.slice(0, 80) : `New message from ${companyName}`);

        if (!ensuredThreadId && threadCreateEndpoint) {
          const created = await createThread({ subject: subj, ch: channel });
          ensuredThreadId = created?.threadId || threadId;
        }

        const atts = attachments.length > 0 ? await buildAttachmentPayload(attachments) : [];
        const result = await createMessage({
          text,
          attachments: atts,
          subject: subj,
          tid: ensuredThreadId || undefined,
        });
        if (!result?.aborted) {
          onSend?.(result?.request);
          if (result?.response) {
            onReceive?.(result.response);
          }
          trackAnalytics('message_received', {});
        }
        dispatch({ type: 'SEND_SUCCESS' });
        return { ok: !result?.aborted, aborted: result?.aborted };
      } catch (err) {
        // Restore the draft if no newer text replaced it while this request was in flight.
        const latestInput = stateRef.current.input;
        if (latestInput === '' || latestInput === inputBeforeSend) {
          dispatch({ type: 'SET_INPUT', input: inputBeforeSend });
        }
        const errorObj = err instanceof Error ? err : new Error(String(err));
        const errorMessage = errorObj.message.toLowerCase();
        const statusMatch = errorMessage.match(/\b(\d{3})\b/);
        const status = statusMatch && statusMatch[1] ? Number(statusMatch[1]) : null;
        const userMessage =
          errorMessage.includes('failed to fetch') ||
          errorMessage.includes('networkerror') ||
          errorMessage.includes('networkerror when attempting to fetch resource')
            ? 'Failed to send. Could not reach the chat API. Please check network/CORS settings.'
            : status === 401 || status === 403
              ? 'Failed to send. Authentication is required. Add a valid widgetAuthToken or API key.'
              : status === 400 || status === 422
                ? 'Failed to send. Please review widget configuration (agent, organization, and message payload).'
                : status === 429
                  ? 'Failed to send. Too many requests right now. Please try again in a moment.'
                  : status === 500 || status === 502 || status === 503 || status === 504
                    ? 'Failed to send. The chat service is temporarily unavailable.'
                    : errorObj.message && errorObj.message !== 'Failed to send'
                      ? `Failed to send: ${errorObj.message}`
                      : 'Failed to send. Please try again.';
        dispatch({ type: 'SEND_ERROR', error: userMessage });
        onError?.(errorObj);
        return { ok: false };
      }
    },
    [
      state.input,
      threadId,
      subject,
      companyName,
      threadCreateEndpoint,
      createThread,
      channel,
      createMessage,
      onSend,
      onReceive,
      onError,
      trackAnalytics,
      buildAttachmentPayload,
    ]
  );

  return {
    state,
    dispatch,
    setMessages,
    createMessage,
    handleSend,
    historyLoaded,
  };
}
